/**
 * SeasonContestantPage — one contestant's season at one association, and the
 * only place the delegate-entered half of their winnings can be set.
 *
 * The breakdown at the bottom is COMPUTED (live standings → payout, never
 * stored) and read-only. Above it a delegate owns three things: the EXTERNAL
 * RESULTS — one row per event won at a rodeo HighCall doesn't run (amount,
 * event, rodeo, date, placement), which roll into "additional" and the total
 * and show in the breakdown highlighted as external; the FINALS ELIGIBLE
 * toggle, the association's call on attendance/dues/conduct; and free-text
 * NOTES. A LEGACY lump amount (from before itemised entry) is shown when it is
 * non-zero, with a Clear action, so it can be re-entered as individual events
 * and retired. See docs/season-winnings.md.
 *
 * `0` and `false` are values a delegate deliberately set — clearing the legacy
 * amount to zero is a real edit, so nothing here may treat them as "unset".
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Coins,
  Loader2,
  MapPin,
  Pencil,
  PiggyBank,
  Plus,
  Trash2,
  Trophy,
  Wallet,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { YearSelect } from '@/components/common/YearSelect';
import { useToast } from '@/contexts/ToastContext';
import {
  useContestantSeason,
  useCreateExternalResult,
  useDeleteExternalResult,
  useUpdateExternalResult,
  useUpsertSeasonAdjustment,
  type ExternalResultInput,
  type SeasonBreakdownRow,
  type SeasonExternalResult,
} from '@/api/extensions/useSeasonWinningsOps';
import { formatCurrency, formatDate, getOrdinal } from '@/utils/formatters';
import { ExternalResultDialog } from './ExternalResultDialog';
import {
  EXTERNAL_ROW_CLASS,
  breakdownRowKey,
  groupByRodeo,
  scopeLabel,
} from './season-breakdown';

/** The attendance rule associations most often write down. */
const TYPICAL_ATTENDANCE_PCT = 33;

export default function SeasonContestantPage() {
  const { orgId = '', userId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const year = Number(searchParams.get('year')) || new Date().getFullYear();

  const { data, isLoading, error } = useContestantSeason(orgId, year, userId);
  const isForbidden = (error as (Error & { status?: number }) | null)?.status === 403;
  const save = useUpsertSeasonAdjustment(orgId, year);
  const createExternal = useCreateExternalResult(orgId, year);
  const updateExternal = useUpdateExternalResult(orgId, year);
  const deleteExternal = useDeleteExternalResult(orgId, year);

  // Form state for the writable fields. Seeded from the server and reset
  // whenever a different season loads, so switching years never carries the
  // previous year's values into the inputs.
  const [notes, setNotes] = useState('');
  // The toggle is optimistic — it flips on click and reverts if the write
  // fails, rather than sitting on the old value until the refetch lands.
  const [eligible, setEligible] = useState(false);
  // The add/edit dialog: closed, adding, or editing one result.
  const [dialog, setDialog] = useState<{ open: boolean; editing: SeasonExternalResult | null }>({
    open: false,
    editing: null,
  });
  // The result a delete is being confirmed for; null = no confirm open.
  const [deleting, setDeleting] = useState<SeasonExternalResult | null>(null);

  const adjustment = data?.adjustment;
  useEffect(() => {
    if (!adjustment) return;
    setNotes(adjustment.notes ?? '');
    setEligible(adjustment.finals_eligible);
  }, [adjustment]);

  const contestant = data?.contestant;
  const rodeoCount = data?.rodeo_count ?? 0;
  const attendancePct =
    rodeoCount > 0 && contestant
      ? Math.round((contestant.rodeos_attended / rodeoCount) * 100)
      : 0;

  const groups = useMemo(
    () => groupByRodeo(contestant?.breakdown ?? []),
    [contestant?.breakdown],
  );

  const backTo = `/delegate/season-roster?org=${orgId}&year=${year}`;

  const handleSaveNotes = () => {
    save.mutate(
      { userId, notes: notes.trim() || null },
      {
        onSuccess: () => showSuccess('Saved', 'Notes updated.'),
        onError: (err) => showError('Could not save notes', (err as Error).message),
      },
    );
  };

  // Retire the pre-itemised lump: `0` is a deliberate value, not "unset".
  const handleClearLegacy = () => {
    save.mutate(
      { userId, additional_winnings: 0 },
      {
        onSuccess: () => showSuccess('Legacy amount cleared'),
        onError: (err) => showError('Could not clear the legacy amount', (err as Error).message),
      },
    );
  };

  const handleSubmitExternal = (values: ExternalResultInput) => {
    const editing = dialog.editing;
    if (editing) {
      updateExternal.mutate(
        { userId, id: editing.id, ...values },
        {
          onSuccess: () => {
            setDialog({ open: false, editing: null });
            showSuccess('Saved', 'Event winnings updated.');
          },
          onError: (err) => showError('Could not save event winnings', (err as Error).message),
        },
      );
      return;
    }
    createExternal.mutate(
      { userId, ...values },
      {
        onSuccess: () => {
          setDialog({ open: false, editing: null });
          showSuccess('Added', `${formatCurrency(values.amount)} at ${values.rodeo_name}.`);
        },
        onError: (err) => showError('Could not add event winnings', (err as Error).message),
      },
    );
  };

  // Delete asks first (the confirm dialog below); the mutation runs only on
  // "Remove". The row is dropped from the list as soon as the delete lands —
  // the hook does not wait on the roster refetch behind it.
  const handleConfirmDelete = () => {
    if (!deleting) return;
    deleteExternal.mutate(
      { userId, id: deleting.id },
      {
        onSuccess: () => {
          setDeleting(null);
          showSuccess('Removed', 'Event winnings removed.');
        },
        onError: (err) => showError('Could not remove event winnings', (err as Error).message),
      },
    );
  };

  const handleToggleEligible = (next: boolean) => {
    setEligible(next);
    save.mutate(
      { userId, finals_eligible: next },
      {
        onSuccess: () =>
          showSuccess(
            next ? 'Marked finals eligible' : 'Finals eligibility removed',
          ),
        onError: (err) => {
          setEligible(!next);
          showError('Could not save finals eligibility', (err as Error).message);
        },
      },
    );
  };

  if (isForbidden) {
    return (
      <div className="space-y-6">
        <BackLink to={backTo} />
        <EmptyState
          icon={Trophy}
          title="Not your association"
          description="You don't hold a role at this association, so this contestant's season isn't yours to read."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink to={backTo} />
        <EmptyState
          icon={Trophy}
          title="Could not load this contestant"
          description={(error as Error).message || 'Unknown error'}
        />
      </div>
    );
  }

  if (isLoading || !data || !contestant) {
    return (
      <div className="space-y-6">
        <BackLink to={backTo} />
        <div className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink to={backTo} />

      {/* Who, and what they are to this association */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {contestant.display_name || 'Unnamed contestant'}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            {contestant.is_member ? (
              <Badge variant="secondary">
                Member{contestant.member_number ? ` #${contestant.member_number}` : ''}
              </Badge>
            ) : (
              <Badge variant="outline">Non-member</Badge>
            )}
            {contestant.finals_eligible && (
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                Finals eligible
              </Badge>
            )}
          </div>
        </div>
        <YearSelect
          value={year}
          onChange={(next) => {
            const params = new URLSearchParams(searchParams);
            params.set('year', String(next ?? new Date().getFullYear()));
            setSearchParams(params, { replace: true });
          }}
          aria-label="Season year"
        />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={Coins}
          label="Managed winnings"
          value={formatCurrency(contestant.managed_winnings)}
          description={`${contestant.events_placed} of ${contestant.events_entered} entries in the money`}
        />
        <StatTile
          icon={PiggyBank}
          label="Additional winnings"
          value={formatCurrency(contestant.additional_winnings)}
          description={
            data.legacy_additional_winnings > 0
              ? `${data.external_results.length} event${data.external_results.length === 1 ? '' : 's'} + legacy amount`
              : `${data.external_results.length} event${data.external_results.length === 1 ? '' : 's'} entered by this association`
          }
        />
        <StatTile
          icon={Wallet}
          label={`${data.season_year} season total`}
          value={formatCurrency(contestant.total_winnings)}
          description="Managed plus additional"
          highlight
        />
        <StatTile
          icon={MapPin}
          label="Rodeos attended"
          value={`${contestant.rodeos_attended} / ${rodeoCount}`}
          description={`${attendancePct}% of the season`}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Totals include go-round money as well as average payouts, so they can read
        higher than this contestant's dashboard Event History.
      </p>

      {/* External results — itemised outside winnings */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold">External results</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Money won at rodeos HighCall doesn't run, one event at a time. Each
              row counts toward the season total and shows under its event on the
              standings.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setDialog({ open: true, editing: null })}
            disabled={createExternal.isPending}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add event winnings
          </Button>
        </div>

        {data.external_results.length === 0 && data.legacy_additional_winnings <= 0 ? (
          <p className="text-sm text-muted-foreground">
            No outside winnings recorded for {data.season_year}.
          </p>
        ) : (
          <Table data-testid="external-results-table">
            <TableHeader>
              <TableRow>
                <TableHead>Rodeo</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Event</TableHead>
                <TableHead className="text-right">Placement</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-24">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.external_results.map((result) => (
                <TableRow key={result.id} data-testid="external-result-row">
                  <TableCell className="font-medium">
                    {result.rodeo_name}
                    <ExternalBadge />
                    {result.notes && (
                      <p className="text-xs font-normal text-muted-foreground mt-0.5">
                        {result.notes}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(result.event_date) || '--'}</TableCell>
                  <TableCell>{result.event_type_key}</TableCell>
                  <TableCell className="text-right">
                    <PlacementBadge placement={result.placement} />
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    {formatCurrency(result.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Edit ${result.event_type_key} at ${result.rodeo_name}`}
                        onClick={() => setDialog({ open: true, editing: result })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={`Delete ${result.event_type_key} at ${result.rodeo_name}`}
                        onClick={() => setDeleting(result)}
                        disabled={deleteExternal.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {data.legacy_additional_winnings > 0 && (
                <TableRow className="bg-muted/50 hover:bg-muted/50" data-testid="legacy-unitemized-row">
                  <TableCell colSpan={4} className="font-medium">
                    Legacy unitemized amount
                    {contestant.notes && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {contestant.notes}
                      </span>
                    )}
                    <p className="text-xs font-normal text-muted-foreground mt-0.5">
                      Re-enter as individual events, then clear.
                    </p>
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    {formatCurrency(data.legacy_additional_winnings)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearLegacy}
                      disabled={save.isPending}
                    >
                      Clear
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <ExternalResultDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ open, editing: open ? prev.editing : null }))}
        seasonYear={data.season_year}
        allowedEventTypeKeys={data.allowed_event_type_keys}
        editing={dialog.editing}
        onSubmit={handleSubmitExternal}
        isPending={createExternal.isPending || updateExternal.isPending}
      />

      {/* Delete confirmation — the house pattern: the shared Dialog as an
          alert dialog (the component library has no AlertDialog; see
          PerformanceSection). Buttons lock while the delete is in flight. */}
      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !deleteExternal.isPending) setDeleting(null);
        }}
      >
        <DialogContent role="alertdialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove this result?</DialogTitle>
            <DialogDescription>
              {deleting
                ? `${formatCurrency(deleting.amount)} — ${deleting.event_type_key} at ${deleting.rodeo_name} will be removed from this contestant's ${data.season_year} season.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deleteExternal.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteExternal.isPending}
            >
              {deleteExternal.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finals eligibility and notes */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4 space-y-4">
        <div>
          <h2 className="text-sm font-semibold">Association entries</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            This association's call on finals eligibility, and private notes.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="finals-eligible"
            checked={eligible}
            onCheckedChange={handleToggleEligible}
            disabled={save.isPending}
          />
          <label htmlFor="finals-eligible" className="text-sm font-medium">
            Finals eligible
          </label>
          <span className="text-xs text-muted-foreground">
            Attended {contestant.rodeos_attended} of {rodeoCount} rodeos (
            {attendancePct}%) — associations typically require{' '}
            {TYPICAL_ATTENDANCE_PCT}%.
          </span>
        </div>

        <div className="space-y-2 border-t pt-4">
          <label
            className="text-xs font-medium text-muted-foreground block"
            htmlFor="adjustment-notes"
          >
            Notes (optional)
          </label>
          <Textarea
            id="adjustment-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything this association should remember about this contestant's season"
            rows={2}
          />
          <Button onClick={handleSaveNotes} disabled={save.isPending} size="sm">
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save notes
          </Button>
        </div>
      </div>

      {/* Breakdown */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold">
          {data.season_year} results at this association
        </h2>
        {groups.length === 0 ? (
          <EmptyState
            icon={Trophy}
            title={`No results in ${data.season_year}`}
            description="Money won at this association's rodeos appears here once the event has been scored; external results appear as they are entered."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Payout</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Placement</TableHead>
                <TableHead className="text-right">Won</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => (
                <Fragment key={group.rodeo_id}>
                  <TableRow className={group.external ? EXTERNAL_ROW_CLASS : 'bg-muted hover:bg-muted'}>
                    <TableCell colSpan={5} className="font-semibold">
                      {group.rodeo_name}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {formatDate(group.start_date) || '--'}
                      </span>
                      {group.external && <ExternalBadge />}
                    </TableCell>
                  </TableRow>
                  {group.rows.map((row) => (
                    <TableRow
                      key={breakdownRowKey(row)}
                      className={row.external ? EXTERNAL_ROW_CLASS : undefined}
                      data-external={row.external ? 'true' : undefined}
                    >
                      <TableCell className="pl-8 font-medium">
                        {row.event_name}
                        {row.external && <ExternalBadge />}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {scopeLabel(row)}
                      </TableCell>
                      <TableCell>{formatDate(row.start_date) || '--'}</TableCell>
                      <TableCell className="text-right">
                        <PlacementBadge placement={row.placement} />
                      </TableCell>
                      <TableCell className="text-right">
                        <PrizeCell row={row} />
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/** Back to the board the user came from, on the same association + season. */
function BackLink({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Season Roster
    </Link>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  description,
  highlight,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  description: string;
  highlight?: boolean;
}) {
  // Same shape as the contestant's own Season Winnings tiles.
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p
        className={`text-2xl font-bold tracking-tight text-center ${
          highlight ? 'text-green-600' : ''
        }`}
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground text-center truncate">{description}</p>
    </div>
  );
}

/** Marks a result a delegate entered from a rodeo HighCall did not run. */
function ExternalBadge() {
  return (
    <Badge
      variant="outline"
      className="ml-2 border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 align-middle"
    >
      External
    </Badge>
  );
}

/** "--" means the payout didn't reach them, not that data is missing. */
function PlacementBadge({ placement }: { placement: number | null }) {
  if (!placement) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      {getOrdinal(placement)}
    </span>
  );
}

/** Their OWN share, with the undivided team figure alongside when it was split. */
function PrizeCell({ row }: { row: SeasonBreakdownRow }) {
  if (!row.prize_amount) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="font-medium text-green-600">
      {formatCurrency(row.prize_amount)}
      {row.team_prize ? (
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          ({formatCurrency(row.team_prize)} team)
        </span>
      ) : null}
    </span>
  );
}
